import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef, useEffect, useState } from "react";

export const HeroSection = () => {
  const videoRef = useRef(null);
  const hasStartedRef = useRef(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    // Reset on mount for new page visits
    hasStartedRef.current = false;
    setIsFadingOut(false);
  }, []);

  useEffect(() => {
    // Skip if already started this mount
    if (isFadingOut || hasStartedRef.current) {
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    // Mark as started to prevent double execution
    hasStartedRef.current = true;

    // Reset video to beginning
    video.currentTime = 0;

    // Try to unmute on any user interaction
    const tryUnmute = () => {
      if (video && video.muted) {
        video.muted = false;
      }
    };
    
    document.addEventListener('click', tryUnmute, { once: true });
    document.addEventListener('touchstart', tryUnmute, { once: true });

    // Wait 1.5 seconds after page load
    const playTimer = setTimeout(() => {
      video.muted = false;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }, 1500);

    // Handle video timeupdate - pause slightly before end to avoid glitch
    let hasTriggeredEnd = false;
    const handleTimeUpdate = () => {
      if (!hasTriggeredEnd && video.duration && video.currentTime >= video.duration - 0.22) {
        hasTriggeredEnd = true;
        video.pause();
        // Hold for 2 seconds, then fade
        setTimeout(() => {
          setIsFadingOut(true);
        }, 2000);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      clearTimeout(playTimer);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      document.removeEventListener('click', tryUnmute);
      document.removeEventListener('touchstart', tryUnmute);
    };
  }, [isFadingOut]);

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-[65vh] flex flex-col items-center justify-center px-6 md:px-12 overflow-hidden"
    >
      {/* Background depth gradient */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(255,253,251,0.9) 0%, rgba(253,251,247,0.5) 50%, transparent 100%)'
        }}
      />
      
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 rounded-full bg-[#0CBCBF]/8 blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-40 right-10 w-96 h-96 rounded-full bg-[#F58227]/8 blur-3xl"
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-3xl mx-auto text-center pt-8">
        {/* Landing Animation - with subtle depth */}
        <motion.div
          animate={{ opacity: isFadingOut ? 0 : 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-4"
        >
          <div 
            className="mx-auto rounded-2xl overflow-hidden"
            style={{ 
              width: '134px', 
              height: '134px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06)'
            }}
          >
            <video
              ref={videoRef}
              src="/landing-animation.mp4"
              poster="/landing-animation-poster.png"
              playsInline
              preload="auto"
              className="object-contain w-full h-full"
            />
          </div>
        </motion.div>

        {/* Core message - immediate clarity */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-6"
        >
          A focus companion
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl md:text-7xl font-extrabold tracking-tight text-[#27272A] mb-6"
        >
          Where was i
        </motion.h1>

        {/* Clear value proposition */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-xl md:text-2xl text-[#52525B] mb-3 leading-relaxed"
        >
          One tap. One thought. Back on track.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-lg text-[#0CBCBF] mb-10 max-w-md mx-auto"
        >
          Lost your train of thought?<br />
          This helps you get it back.
        </motion.p>

        {/* Concrete CTAs - with depth */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col gap-3 justify-center items-center"
        >
          <Link
            to="/app"
            data-testid="hero-cta-start"
            className="bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-10 py-4 text-lg font-bold transition-all active:scale-95"
            style={{
              boxShadow: '0 2px 8px rgba(245, 130, 39, 0.3), 0 8px 24px rgba(245, 130, 39, 0.2)'
            }}
          >
            Start Free
          </Link>
          <span className="text-[15px] text-stone-500">No sign-up required. Just pick it up and go.</span>
          <button
            onClick={() => {
              const element = document.getElementById("how-it-works");
              if (element) {
                const yOffset = -100; // Offset to account for bottom ribbon
                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: "smooth" });
              }
            }}
            className="text-[13px] text-stone-700 font-medium hover:text-[#0CBCBF] transition-colors mt-1 flex flex-col items-center gap-0.5 md:gap-0"
          >
            <span>See how it works (40 sec)</span>
            <ArrowDown size={20} className="text-stone-400" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
